import React, { useState, useMemo } from "react";
import { X, Download, Database, Lock, AlertTriangle } from "lucide-react";
import { listProfiles, getLatest } from "@/lib/profiles/storage";
import { PROFILE_KIND_LABELS } from "@/lib/profiles/schemas";
import { resolve } from "@/lib/rce/resolver";

// PullFromProfilesDrawer — reusable cross-profile auto-fill UI (P1).
//
// The drawer presents a target form's empty (or stale) fields next to the
// best candidate values from existing profiles. The user selects which
// profile rows to consume, previews the result, and applies. The target
// form receives a `{ patch, importedFrom }` payload it merges into its
// own state.
//
// Props:
//   open          — boolean, controls visibility
//   target        — current form payload (read-only here)
//   mappings      — array of FieldMapping (see below)
//   onApply(merged, importedFrom) — called when the user clicks Apply
//   onClose       — called when the user dismisses the drawer
//   title         — drawer header text (e.g. "Pull values into Operational State")
//   rceCtx        — optional resolver context ({ wellId, rigId, ... }). When
//                    supplied, mappings with an `rcePath` resolve against the
//                    CANONICAL RCE stores (Well Profiles / Rig Data Sheets /
//                    Mud Programs, via the active Engineering Session) first,
//                    falling back to the legacy sourceKind/sourceField lookup
//                    below when the RCE resolver has no value.
//
// FieldMapping shape:
//   {
//     section:     "Depths" | "Mud" | …             // group label
//     field:       "currentMd"                       // key on target payload
//     label:       "Current MD"                      // human label
//     unit:        "ft"                              // optional suffix
//     sourceKind:  "well" | "mud" | "pump" | "casing" | "bha"
//     sourceField: "currentMd"                       // dotted-path on profile.payload
//     rcePath:     "well.currentMdFt"                 // optional — see rceCtx above
//   }
//
// "Never overwrite populated values without confirmation" — by default
// rows whose target field is non-empty render with a guarded toggle
// (off). Empty fields default to on. The user can flip any row.

export default function PullFromProfilesDrawer({
  open,
  title = "Pull values from existing profiles",
  target,
  mappings,
  onApply,
  onClose,
  rceCtx = null,
  // canonicalOnly (Operational State correction) — hides the legacy
  // "choose source profiles" selects entirely; every mapping's candidate
  // comes strictly from the RCE resolver. Does NOT add a second resolver
  // path or change RCE precedence — candidateFor()/_rceResult() below are
  // unchanged and used exactly the same way as for the legacy consumer.
  // `manual: true` mappings are pulled out of the resolvable table and
  // rendered instead as plain "Manual / Live Values" info rows.
  canonicalOnly = false,
  applyButtonLabel = null,
}) {
  // Selected profile id per source kind.
  // Canonical-only mappings carry no `sourceKind` (nothing in the legacy store
  // corresponds to them). They are excluded here because `listProfiles()`
  // throws on an unrecognised kind — see lib/profiles/storage.js#_assertKind.
  const usedKinds = useMemo(
    () => Array.from(new Set(mappings.map((m) => m.sourceKind).filter(Boolean))),
    [mappings],
  );
  const [selectedIds, setSelectedIds] = useState(
    () => Object.fromEntries(usedKinds.map((k) => [k, ""])),
  );

  // "Pull From All Bound Profiles" shortcut (P2 v6): auto-selects the
  // latest profile of each source kind AND turns ON every include flag
  // that has a resolvable candidate. Preserves the existing audit-trail
  // and overwrite-protection logic — the user still reviews before Apply.
  const handlePullFromAll = () => {
    const next = { ...selectedIds };
    usedKinds.forEach((k) => {
      if (next[k]) return;                     // don't clobber an explicit choice
      const rows = _safeList(k);
      if (rows.length > 0) next[k] = rows[0].id;
    });
    setSelectedIds(next);
    const incl = { ...includes };
    mappings.forEach((m) => {
      const present = target?.[m.field] != null && target[m.field] !== "";
      // Preserve overwrite protection: do NOT auto-enable rows whose
      // target field is already populated. Empty targets default ON.
      if (!present) incl[m.field] = true;
    });
    setIncludes(incl);
  };
  // Per-mapping include flags. Default: include only if target field empty.
  const initialIncludes = useMemo(() => {
    const map = {};
    mappings.forEach((m) => {
      const present = target?.[m.field] != null && target[m.field] !== "";
      map[m.field] = !present;
    });
    return map;
  }, [mappings, target]);
  const [includes, setIncludes] = useState(initialIncludes);

  // Reset state if the drawer reopens.
  React.useEffect(() => {
    if (open) {
      setSelectedIds(Object.fromEntries(usedKinds.map((k) => [k, ""])));
      setIncludes(initialIncludes);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // A5 — a kind's legacy picker is hidden when it has no legacy records AND
  // every mapping of that kind already resolves from the canonical stores. In
  // that state the picker offers nothing and only misrepresents the workflow.
  // Kinds that still carry legacy-only mappings keep their picker, because for
  // those fields the legacy record genuinely is the only source.
  function _legacyPickerNeeded(k) {
    if (_safeList(k).length > 0) return true;
    const ofKind = mappings.filter((m) => m.sourceKind === k);
    return !ofKind.every((m) => _rceResult(m) != null);
  }

  // Resolve the source profile payload for each kind.
  const sourceRows = Object.fromEntries(
    usedKinds.map((k) => {
      const id = selectedIds[k];
      const row = id ? getLatest(k, id) : null;
      return [k, row];
    }),
  );

  // For each mapping, compute the candidate value (raw string). Checks the
  // canonical RCE resolver first (when rcePath + rceCtx are available), then
  // falls back to the legacy profile-store lookup. Returns null if neither
  // resolves — never fabricates a value.
  function _rceResult(m) {
    if (!m.rcePath || !rceCtx) return null;
    // Drawer-confined Hole Section override: when this specific mapping is
    // well.holeSection and the caller's rceCtx carries an explicit session
    // Hole Section (see OperationalStateProfileForm), prefer it over the
    // casing-derived value. Every other rcePath/mapping is unaffected, and
    // the shared resolver/deriver precedence is untouched - callers that
    // don't pass ctx.holeSection see identical behavior to before.
    if (m.rcePath === "well.holeSection" && rceCtx.holeSection) {
      return { value: rceCtx.holeSection, confidence: "exact", source: { module: "session", id: "", label: "Engineering Session" }, conflicts: [] };
    }
    const r = resolve(m.rcePath, rceCtx);
    if (!r || r.confidence === "unknown" || r.confidence === "conflict") return null;
    if (r.value === "" || r.value == null) return null;
    return r;
  }
  function candidateFor(m) {
    const rce = _rceResult(m);
    if (rce) return String(rce.value);
    const src = sourceRows[m.sourceKind];
    if (!src) return null;
    const v = _readPath(src.payload, m.sourceField);
    return v != null && v !== "" ? String(v) : null;
  }
  function candidateSourceLabel(m) {
    const rce = _rceResult(m);
    if (rce) return rce.source?.label || "Engineering Context";
    const src = sourceRows[m.sourceKind];
    return src ? `${src.label} · v${src.version}` : null;
  }

  const handleApply = () => {
    const patch = {};
    const importedFrom = {};
    mappings.forEach((m) => {
      if (!includes[m.field]) return;
      const candidate = candidateFor(m);
      if (candidate == null) return;
      patch[m.field] = candidate;
      const rce = _rceResult(m);
      if (rce) {
        importedFrom[m.field] = {
          kind:    "rce",
          id:      rce.source?.id || "",
          version: "",
          field:   m.rcePath,
          at:      new Date().toISOString(),
        };
        return;
      }
      const src = sourceRows[m.sourceKind];
      importedFrom[m.field] = {
        kind:    m.sourceKind,
        id:      src.id,
        version: src.version,
        field:   m.sourceField,
        at:      new Date().toISOString(),
      };
    });
    onApply(patch, importedFrom);
  };

  // canonicalOnly splits mappings into the resolvable table (RCE-backed
  // candidates) and a plain manual/live info list. Non-canonical consumers
  // (Well Profile) keep every mapping in the resolvable table, unchanged.
  const manualMappings = canonicalOnly ? mappings.filter((m) => m.manual) : [];
  const tableMappings  = canonicalOnly ? mappings.filter((m) => !m.manual) : mappings;

  // Group mappings by section for the UI.
  const sections = tableMappings.reduce((acc, m) => {
    const sec = m.section || "Fields";
    (acc[sec] = acc[sec] || []).push(m);
    return acc;
  }, {});

  const importableCount = tableMappings.reduce((n, m) => {
    if (!includes[m.field]) return n;
    return candidateFor(m) != null ? n + 1 : n;
  }, 0);

  return (
    <div data-testid="pull-from-profiles-drawer"
         className="fixed inset-0 z-[10050] flex"
         role="dialog" aria-modal="true">
      <button data-testid="pull-from-profiles-scrim" aria-label="Close"
              onClick={onClose}
              className="flex-1"
              style={{ background: "rgba(0,0,0,0.55)" }} />
      <aside className="h-full overflow-y-auto"
             style={{
               width: "min(640px, 100vw)",
               background: "var(--rs-bg-1, #0A0B0E)",
               borderLeft: "1px solid var(--rs-border)",
             }}>
        <header className="sticky top-0 z-10 flex items-center justify-between px-4 py-3"
                style={{ background: "rgba(10,11,14,0.96)", borderBottom: "1px solid var(--rs-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]">
              Auto-populate · {importableCount} field{importableCount === 1 ? "" : "s"} ready
            </div>
            <h3 className="text-[14px] font-semibold text-[var(--rs-text-1)]">{title}</h3>
          </div>
          <button data-testid="pull-from-profiles-close" type="button" onClick={onClose}
                  className="p-1.5 rounded hover:text-[var(--rs-amber)] text-[var(--rs-text-2)]">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="px-4 py-3 space-y-4">
          {/* Source profile pickers — hidden entirely for canonicalOnly
              consumers (Operational State). Those values resolve strictly
              from the RCE resolver; there is no source left to pick. */}
          {!canonicalOnly && (
            <section data-testid="pull-from-profiles-sources" className="card-rs p-3">
              <div className="flex items-baseline justify-between mb-2 gap-2">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)]">
                  Choose source profiles
                </div>
                <button type="button"
                        data-testid="pull-from-all"
                        onClick={handlePullFromAll}
                        className="text-[10.5px] px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1"
                        style={{
                          background: "rgba(245,180,0,0.10)",
                          color: "var(--rs-amber)",
                          border: "1px solid rgba(245,180,0,0.35)",
                          minHeight: 28,
                        }}>
                  Pull from all bound profiles
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {usedKinds.map((k) => {
                  const rows = _safeList(k);
                  if (!_legacyPickerNeeded(k)) {
                    // No legacy records and everything of this kind already
                    // resolves canonically — name the source instead of offering
                    // an empty picker.
                    const resolved = mappings.find(
                      (m) => m.sourceKind === k && _rceResult(m) != null,
                    );
                    return (
                      <div key={k} className="flex items-center gap-2"
                           data-testid={`pull-source-canonical-${k}`}>
                        <span className="text-[11px] uppercase font-mono text-[var(--rs-text-3)] w-16">{k}</span>
                        <span className="flex-1 text-[11px] text-[var(--rs-text-2)] inline-flex items-center gap-1">
                          <Database className="w-3 h-3" style={{ color: "var(--rs-amber)" }} />
                          {resolved ? candidateSourceLabel(resolved) : "Engineering Context"}
                        </span>
                      </div>
                    );
                  }
                  return (
                    <div key={k} className="flex items-center gap-2">
                      <span className="text-[11px] uppercase font-mono text-[var(--rs-text-3)] w-16">{k}</span>
                      <select data-testid={`pull-source-${k}`}
                              value={selectedIds[k] || ""}
                              onChange={(e) => setSelectedIds({ ...selectedIds, [k]: e.target.value })}
                              className="input-rs flex-1 text-[12px]" style={{ minHeight: 32 }}>
                        <option value="">— select {PROFILE_KIND_LABELS[k]?.replace(/ Profiles?$/, "") || k} —</option>
                        {rows.map((r) => (
                          <option key={r.id} value={r.id}>{r.label} · v{r.version}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {canonicalOnly && (
            <div data-testid="pull-canonical-heading"
                 className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-amber)]">
              Available from Engineering Setup
            </div>
          )}

          {/* Preview & toggles per section */}
          {Object.entries(sections).map(([sectionLabel, items]) => (
            <section key={sectionLabel} data-testid={`pull-section-${sectionLabel}`}
                     className="card-rs p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-2">
                {sectionLabel}
              </div>
              <table className="w-full text-[11.5px]">
                <thead>
                  <tr className="text-[9.5px] uppercase tracking-[0.10em] text-[var(--rs-text-3)]">
                    <th className="text-left py-1 w-8"></th>
                    <th className="text-left py-1">Field</th>
                    <th className="text-left py-1">Current</th>
                    <th className="text-left py-1">From profile</th>
                    <th className="text-left py-1">New value</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((m) => {
                    const cand = candidateFor(m);
                    const present = target?.[m.field] != null && target[m.field] !== "";
                    const willOverwrite = present && cand != null && includes[m.field];
                    const srcLabel = candidateSourceLabel(m);
                    const disabled = cand == null;
                    return (
                      <tr key={m.field}
                          data-testid={`pull-row-${m.field}`}
                          className="border-t" style={{ borderColor: "rgba(148,163,184,0.18)" }}>
                        <td className="py-1.5">
                          <input type="checkbox" data-testid={`pull-toggle-${m.field}`}
                                 checked={!!includes[m.field]} disabled={disabled}
                                 onChange={(e) => setIncludes({ ...includes, [m.field]: e.target.checked })} />
                        </td>
                        <td className="py-1.5 text-[var(--rs-text-2)]">
                          <div>{m.label}</div>
                          {m.unit && (
                            <span className="text-[9.5px] text-[var(--rs-text-3)]">{m.unit}</span>
                          )}
                        </td>
                        <td className="py-1.5 font-mono text-[var(--rs-text-3)]">
                          {present ? String(target[m.field]) : "—"}
                        </td>
                        <td className="py-1.5 text-[10.5px] text-[var(--rs-text-3)]">
                          {srcLabel
                            ? <span className="inline-flex items-center gap-1">
                                <Database className="w-3 h-3" />
                                {srcLabel}
                              </span>
                            : <span className="text-[var(--rs-text-3)]">no source</span>}
                          {/* A7 — display only. Surfaced when the canonical
                              resolver returns this field at "derived"
                              confidence, which is how an approximated value is
                              already published. Importing is not blocked. */}
                          {m.approxNote && _rceResult(m)?.confidence === "derived" && (
                            <div data-testid={`pull-approx-${m.field}`}
                                 className="mt-1 inline-flex items-start gap-1 text-[10px] leading-snug"
                                 style={{ color: "#FCA5A5" }}>
                              <AlertTriangle className="w-3 h-3 shrink-0 mt-[1px]" />
                              <span>{m.approxNote}</span>
                            </div>
                          )}
                        </td>
                        <td className="py-1.5 font-mono">
                          {cand == null ? (
                            <span className="text-[var(--rs-text-3)]">—</span>
                          ) : willOverwrite ? (
                            <span className="text-[#F5B400] inline-flex items-center gap-1" title="Overwrite">
                              <Lock className="w-3 h-3" /> {cand}
                            </span>
                          ) : (
                            <span className="text-[var(--rs-amber)]">{cand}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          ))}

          {canonicalOnly && manualMappings.length > 0 && (
            <section data-testid="pull-manual-values" className="card-rs p-3">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--rs-text-3)] mb-2">
                Manual / Live Values
              </div>
              <div className="space-y-0">
                {manualMappings.map((m) => {
                  const present = target?.[m.field] != null && target[m.field] !== "";
                  return (
                    <div key={m.field} data-testid={`pull-manual-row-${m.field}`}
                         className="flex items-center justify-between gap-2 text-[11.5px] py-1.5"
                         style={{ borderTop: "1px solid rgba(148,163,184,0.14)" }}>
                      <div className="text-[var(--rs-text-2)]">
                        {m.label}
                        {m.unit && <span className="text-[9.5px] text-[var(--rs-text-3)] ml-1">{m.unit}</span>}
                      </div>
                      <div className="text-right">
                        <div className="font-mono text-[var(--rs-text-3)]">{present ? String(target[m.field]) : "—"}</div>
                        <div className="text-[9.5px] text-[var(--rs-text-3)]">{m.manualNote || "Enter manually"}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Sticky footer — the drawer aside is the scrolling container
              (overflow-y-auto), so this pins Cancel/Apply to its visible
              bottom edge regardless of content height. Fixes the app's
              bottom-nav (zIndex 10000) intercepting clicks on a tall
              drawer's footer at real viewport heights. */}
          <div className="sticky bottom-0 flex items-center justify-end gap-2 pt-2 pb-3 -mx-4 px-4"
               style={{ background: "var(--rs-bg-1, #0A0B0E)", borderTop: "1px solid var(--rs-border)" }}>
            <button type="button" onClick={onClose}
                    data-testid="pull-from-profiles-cancel"
                    className="px-4 py-2 rounded-md text-[13px]"
                    style={{ background: "transparent", color: "var(--rs-text-2)", border: "1px solid var(--rs-border)", minHeight: 40 }}>
              Cancel
            </button>
            <button type="button" onClick={handleApply}
                    disabled={importableCount === 0}
                    data-testid="pull-from-profiles-apply"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium disabled:opacity-40"
                    style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 40 }}>
              <Download className="w-4 h-4" />
              {applyButtonLabel || `Apply ${importableCount} value${importableCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}

// listProfiles() throws on an unrecognised kind and can also surface a storage
// read error; neither should take the drawer down.
function _safeList(kind) {
  try { return listProfiles(kind) || []; } catch { return []; }
}

function _readPath(obj, dottedPath) {
  if (!obj) return null;
  const parts = dottedPath.split(".");
  let cur = obj;
  for (const p of parts) {
    if (cur == null) return null;
    cur = cur[p];
  }
  return cur;
}
